import { Eye, Merge } from "lucide-react";
import { motion } from "motion/react";
import { useTranslation } from "react-i18next";

import { SEGMENT_TRANSITION } from "../constants";
import { FixAlert } from "./FixAlert";
import { MergePreview, MergeSummary, type MergeSource } from "./MergePreview";

import { Button } from "@/components/ui/v2/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/v2/popover";
import type { OverlapGroup, SegmentField } from "@/features/segments/types";

type OverlapNoticeProps = {
  groups: OverlapGroup[];
  fields: SegmentField[];
  sourcesOf: (group: OverlapGroup) => MergeSource[];
  onFixAll: () => void;
};

export function OverlapNotice({
  groups,
  fields,
  sourcesOf,
  onFixAll,
}: OverlapNoticeProps) {
  const { t } = useTranslation();

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.98 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.98 }}
      transition={SEGMENT_TRANSITION}
    >
      <FixAlert
        variant="info"
        role="status"
        icon={Merge}
        fixIcon={Merge}
        title={t("admin.segments.builder.overlap.title", {
          count: groups.length,
        })}
        description={t("admin.segments.builder.overlap.description")}
        fixLabel={t("admin.segments.builder.overlap.fixAll")}
        actions={
          <Popover>
            <PopoverTrigger asChild>
              <Button
                type="button"
                variant="outline"
                data-testid="overlap-preview-all"
              >
                <Eye />
                {t("admin.segments.builder.overlap.preview")}
              </Button>
            </PopoverTrigger>
            <PopoverContent
              align="end"
              className="max-h-[min(70vh,36rem)] w-[28rem] max-w-[calc(100vw-2rem)] overflow-y-auto p-0"
              data-testid="overlap-preview"
            >
              <ul className="divide-y">
                {groups.map((group) => (
                  <li
                    key={group.key}
                    className="flex flex-col gap-3 p-4"
                    data-testid="overlap-preview-group"
                    data-field={group.field}
                  >
                    <p className="text-sm font-medium">
                      <MergeSummary
                        group={group}
                        sources={sourcesOf(group)}
                        fields={fields}
                      />
                    </p>
                    <MergePreview
                      group={group}
                      sources={sourcesOf(group)}
                      fields={fields}
                    />
                  </li>
                ))}
              </ul>
            </PopoverContent>
          </Popover>
        }
        testId="overlap-notice"
        fixTestId="overlap-fix-all"
        onFix={onFixAll}
      />
    </motion.div>
  );
}
